import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Card,
  Stack,
  Typography,
  FormControlLabel,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { selectSelectedConnection } from "@/features/connections/integrationSlice";
import { useDispatch, useSelector } from "react-redux";
import ShepCheckbox from "@/components/GenericComponents/ShepCheckbox";
import { AddIcon, DeleteBinLine } from "@/ImageRepo";
import axios from "@/features/axiosHelper";
import ShepButton from "@/components/GenericComponents/ShepButton";
import { createAlert } from "@/features/homepage/alertSliceList";

const EmailConnectionSettings = () => {
  const dispatch = useDispatch();

  const selectedConnection = useSelector(selectSelectedConnection);
  const [emailSettings, setEmailSettings] = useState({});

  const [useDomainMatch, setUseDomainMatch] = useState(false);
  const [domainValue, setDomainValue] = useState("");
  const [emailAddresses, setEmailAddresses] = useState([]);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [disableSave, setDisableSave] = useState(true);

  useEffect(() => {
    GetExistingSettings();
  }, []);

  useEffect(() => {
    setUseDomainMatch(emailSettings.UseDomainMatch);
    setDomainValue(emailSettings.Domain);
    setEmailAddresses(emailSettings.EmailAddresses ?? []);
  }, [emailSettings]);

  useEffect(() => {
    setDisableSave(!ValidateData());
    const { UseDomainMatch, Domain, EmailAddresses } = emailSettings;
    if (
      UseDomainMatch !== useDomainMatch ||
      Domain !== domainValue ||
      EmailAddresses?.filter((email) => !emailAddresses.includes(email))
        .length !== 0 ||
      emailAddresses?.filter((email) => !EmailAddresses?.includes(email))
        .length !== 0
    ) {
      setUnsavedChanges(true);
    } else {
      setUnsavedChanges(false);
    }
  }, [useDomainMatch, domainValue, emailAddresses, emailSettings]);

  const GetExistingSettings = () => {
    axios
      .get("/connections/email-settings", {
        params: {
          connectionId: selectedConnection.id,
        },
      })
      .then((response) => {
        setEmailSettings(response.data);
      })
      .catch((err) => {
        dispatch(
          createAlert({
            status: "Error",
            message: "Connection could not be retrieved: " + err.message,
          }),
        );
      });
  };

  const handleSave = () => {
    const EmailSettings = {
      EmailAddresses: emailAddresses,
      UseDomainMatch: useDomainMatch,
      Domain: domainValue,
    };
    axios
      .post("/connections/email-settings", EmailSettings, {
        params: { connectionId: selectedConnection.id },
      })
      .then((response) => {
        if (response.data === selectedConnection.id) {
          dispatch(
            createAlert({
              status: "Success",
              alertType: "result",
              title: "Connection Updated!",
              message: "Connection is updated correctly.",
              buttonLabel: "Close",
            }),
          );
          GetExistingSettings();
        } else {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              title: "Unable to Update Connection.",
              message: "Connection could not be updated.",
              buttonLabel: "Close",
            }),
          );
        }
      })
      .catch((err) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "result",
            title: "Unable to Update Connection.",
            message: "Connection could not be updated: " + err.message,
            buttonLabel: "Close",
          }),
        );
      });
  };

  const ValidateData = () => {
    if (useDomainMatch && domainValue.trim() === "") return false;
    else if (emailAddresses.length === 0 && !useDomainMatch) return false;
    return true;
  };
  return (
    <Container>
      <Card className="global-settings-card">
        <Stack>
          <Typography variant="h2">Connection Matching / Whitelist</Typography>
          <Box
            sx={{ marginBottom: "60px", marginTop: "30px", minHeight: "50px" }}
          >
            <FormControlLabel
              control={
                <ShepCheckbox
                  enabled={true}
                  onClick={() => setUseDomainMatch(!useDomainMatch)}
                  checked={useDomainMatch}
                />
              }
              label="Identify Connection by email domain"
            />
            {useDomainMatch && (
              <TextField
                placeholder={"domain to match"}
                value={domainValue}
                onChange={(event) => setDomainValue(event.target.value)}
                sx={{
                  paddingLeft: "10px",
                  width: "450px",
                  "&:hover fieldset": {
                    backgroundColor: "#ffffff",
                  },
                  "&.MuiFormControl-root .MuiInputBase-root": {
                    borderRadius: "5px",
                  },
                  "&:hover fieldset": {
                    color: "#000000",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">@</InputAdornment>
                  ),
                }}
              />
            )}
          </Box>
          <WhitelistedEmailsList
            emails={emailAddresses}
            setEmails={setEmailAddresses}
          />
        </Stack>
        <ShepButton disabled={disableSave} onClick={handleSave}>
          Save
        </ShepButton>
        {unsavedChanges && (
          <Typography variant="body2" sx={{ color: "var(--error-primary)" }}>
            There are unsaved changes
          </Typography>
        )}
      </Card>
    </Container>
  );
};

const WhitelistedEmailsList = ({ emails, setEmails }) => {
  const [currentText, setCurrentText] = useState("");

  const handleChange = (event) => {
    setCurrentText(event.target.value);
  };
  const handleAddEmail = () => {
    if (currentText === "") return;
    if (emails.filter((x) => x == currentText).length > 0) return;
    const allEmails = [...emails, currentText];
    setEmails(allEmails);
  };
  const handleRemoveEmail = ({ email }) => {
    const newList = emails.filter((x) => x !== email);
    setEmails(newList);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "left",
          marginBottom: "15px",
        }}
      >
        <TextField
          variant="outlined"
          label="Add Email Address"
          onChange={handleChange}
          sx={{ width: 400 }}
        />
        <IconButton onClick={handleAddEmail} label={"Add"} iconSize={30}>
          <AddIcon />
        </IconButton>
      </Box>
      <Card sx={{ maxHeight: "200px", overflowY: "auto" }}>
        {emails?.map((email, index) => {
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
              key={index}
            >
              <Typography variant="body2">{email}</Typography>
              <IconButton
                onClick={() => handleRemoveEmail({ email })}
                label={"Remove"}
                iconSize={30}
              >
                <DeleteBinLine />
              </IconButton>
            </Box>
          );
        })}
      </Card>
    </>
  );
};

export default EmailConnectionSettings;
